import _ from 'underscore';

import { IIdItemInfo } from './resourceInfoParts';

export class Set<T> {

  private items: _.Dictionary<T>;
  private keyForItem: (item: T) => string;

  constructor(keyForItem: (item: T) => string) {
    this.items = {};
    this.keyForItem = keyForItem;
  }

  public static fromKeyArray(keys: ArrayLike<string>): Set<string> {
    const set = new Set(Set.Identity);
    set.addItems(keys);
    return set;
  }

  public static fromIdItemArray<T extends IIdItemInfo>(items: ArrayLike<T>): Set<T> {
    const set = new Set<T>(Set.PluckId);
    set.addItems(items);
    return set;
  }

  public static fromNumberArray(nums: ArrayLike<number>): Set<number> {
    const set = new Set(Set.IdentityNumber);
    set.addItems(nums);
    return set;
  }

  public static singleton<T>(item: T, keyForItem: (item: T) => string) {
    const set = new Set(keyForItem);
    set.add(item);
    return set;
  }

  public static singletonForKeyItem(item: string): Set<string> {
    const set = new Set(Set.Identity);
    set.add(item);
    return set;
  }

  public static singletonForIdItem<T extends IIdItemInfo>(item: T): Set<T> {
    const set = new Set<T>(Set.PluckId);
    set.add(item);
    return set;
  }

  public keyFor(item: T): string {
    return this.keyForItem(item);
  }

  public containsKey(key: string): boolean {
    return _.has(this.items, key);
  }

  public contains(item: T): boolean {
    return this.containsKey(this.keyForItem(item));
  }

  public size(): number {
    return _.size(this.items);
  }

  public isEmpty(): boolean {
    return _.isEmpty(this.items);
  }

  public keys(): string[] {
    return _.keys(this.items);
  }

  public get(itemId: string) {
    return this.items[itemId];
  }

  public add(item: T): void {
    this.items[this.keyForItem(item)] = item;
  }

  public addItems(items: ArrayLike<T> | Set<T>): void {
    const iterator = (item: T) => this.add(item);
    if (items instanceof Set) {
      _.each(items.items, iterator);
    } else {
      _.each(items, iterator);
    }
  }

  public withItems(items: ArrayLike<T> | Set<T>): Set<T> {
    this.addItems(items);
    return this;
  }


  // Updates the value of the given item if it is already in the set.
  // This is useful if the value corresponding to some key may have changed.
  public update(item: T) {
    if (this.contains(item)) {
      this.add(item);
    }
  }

  public updateItems(items: ArrayLike<T>) {
    _.each(items, (item: T) => {
      this.update(item);
    });
  }

  public remove(item: T) {
    delete this.items[this.keyForItem(item)];
  }

  public removeItems(items: ArrayLike<T>) {
    _.each(items, (item: T) => {
      this.remove(item);
    });
  }

  public removeItemsIn<U>(set: Set<U>) {
    _.each(this.items, (item: T, key: string) => {
      if (set.containsKey(key)) {
        this.remove(item);
      }
    });
  }

  public removeItemsNotIn<U>(set: Set<U>) {
    _.each(this.items, (item: T, key: string) => {
      if (!set.containsKey(key)) {
        this.remove(item);
      }
    });
  }

  public toggle(item: T): boolean {
    if (this.contains(item)) {
      this.remove(item);
      return false;
    } else {
      this.add(item);
      return true;
    }
  }

  public toggleItems(items: ArrayLike<T>): void {
    _.each(items, (item: T) => {
      this.toggle(item);
    });
  }

  public clear(): void {
    this.items = {};
  }

  public toArray(): T[] {
    return _.values(this.items);
  }

  public each(iterator: (item: T) => void, context?: any): void {
    _.each(this.items, iterator, context);
  }

  public find(iterator: (item: T) => boolean, context?: any): any {
    return _.find(this.items, iterator, context);
  }

  public pluck(propertyName: string): any[] {
    return _.pluck(this.toArray(), propertyName);
  }

  public map<TResult>(iterator: _.ObjectIterator<T, TResult>, context?: any): TResult[] {
    return _.map(this.items, iterator, context);
  }

  public reduce<TResult>(iterator: _.MemoObjectIterator<T, TResult>, memo?: TResult, context?: any): TResult {
    return _.reduce(this.items, iterator, memo, context);
  }

  public sum(iterator: (item: T) => number): number {
    return this.reduce((sum: number, item: T) => sum + iterator(item), 0);
  }

  public all(iterator: _.ObjectIterator<T, boolean>): boolean {
    return _.all(this.items, iterator);
  }

  public any(iterator: _.ObjectIterator<T, boolean>): boolean {
    return _.any(this.items, iterator);
  }

  public filter(filterFunction: (item: T) => boolean): Set<T> {
    const filteredSet: Set<T> = new Set<T>(this.keyForItem);
    this.each((item: T) => {
      if (filterFunction(item)) {
        filteredSet.add(item);
      }
    });
    return filteredSet;
  }

  public clone(): Set<T> {
    const clone = new Set<T>(this.keyForItem);
    clone.addItems(this.toArray());
    return clone;
  }

  public first(): T {
    return this.get(this.keys()[0]);
  }

  public getByIndex(index: number): T {
    return this.get(this.keys()[index]);
  }

  /**
   * Returns true iff this set contains the same key/item pairs as the other
   * set, as determined by reference equality. We can't use standard deep equals
   * algorithms to compare two sets, because the keyForItem functions may be
   * different objects even if they behave the same.
   */
  public containsSameItemsAs(other: Set<any>): boolean {
    if (this.size() !== other.size()) {
      return false;
    }
    return this.all((item, key) => item === other.get(key));
  }
}

export namespace Set {
  export function Identity(x: string): string { return x; }
  export function PluckId<T extends IIdItemInfo>(item: T) { return item.id; }
  export function IdentityNumber(n: number): string { return n.toString(); }
}
